import { Card, Button, Form } from "react-bootstrap";
import "../styles/form.css";
import { useState } from "react";
import Container from "react-bootstrap/Container";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faPhone,
  faEnvelope,
  faLocationDot,
  faSackDollar,
  faCoins,
  faCircleCheck,
  faTriangleExclamation,
  faArrowRight,
  faLock,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const LEAD_TYPE_LABELS = {
  Mortgage: "Jelzáloghitel",
  Personal_loan: "Személyi kölcsön",
  retail_account: "Lakossági bankszámla",
  Pension: "Nyugdíjmegtakarítás",
  risk_insurance: "Kockázati életbiztosítás",
};

const FIELDS = [
  {
    name: "name",
    label: "Név",
    icon: faUser,
    placeholder: "pl.: Minta János",
    feedback: "Kérlek add meg a neved.",
  },
  {
    name: "phone",
    label: "Telefonszám",
    icon: faPhone,
    placeholder: "pl.: +36 20 123 4567",
    feedback: "Kérlek adj meg egy érvényes telefonszámot.",
  },
  {
    name: "email",
    label: "Email cím",
    icon: faEnvelope,
    type: "email",
    placeholder: "pl.: janos@example.hu",
    feedback: "Kérlek adj meg egy érvényes email címet.",
  },
  {
    name: "city",
    label: "Település",
    icon: faLocationDot,
    placeholder: "pl.: Budapest",
    feedback: "Kérlek add meg a településed.",
  },
  {
    name: "income",
    label: "Havi jövedelem (nettó, Ft)",
    icon: faSackDollar,
    thousandSeparated: true,
    placeholder: "pl.: 450 000",
    feedback: "Kérlek add meg a havi nettó jövedelmed.",
  },
];

const LOAN_AMOUNT_FIELD = {
  name: "loanAmount",
  label: "Igényelt hitelösszeg (Ft)",
  icon: faCoins,
  thousandSeparated: true,
  placeholder: "pl.: 3 000 000",
  feedback: "Kérlek add meg az igényelt hitelösszeget.",
};

function formatThousands(value) {
  const digitsOnly = value.replace(/\D/g, "");
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

const LOAN_LEAD_TYPES = ["Mortgage", "Personal_loan"];

/**
 * @param {string} leadType "Mortgage" | "Personal_loan" | "retail_account" | "Pension" | "risk_insurance"
 */
export default function RetailLeadForm({ leadType }) {
  const [validated, setValidated] = useState(false);
  const [status, setStatus] = useState("idle");
  const [thousandValues, setThousandValues] = useState({});
  const navigate = useNavigate();

  const handleThousandChange = (name, rawValue) => {
    setThousandValues((prev) => ({ ...prev, [name]: formatThousands(rawValue) }));
  };

  const leadTypeLabel = LEAD_TYPE_LABELS[leadType] || leadType || "Általános";
  const isLoanLeadType = LOAN_LEAD_TYPES.includes(leadType);
  const incomeIndex = FIELDS.findIndex((field) => field.name === "income");
  const fields = isLoanLeadType
    ? [
        ...FIELDS.slice(0, incomeIndex),
        LOAN_AMOUNT_FIELD,
        ...FIELDS.slice(incomeIndex),
      ]
    : FIELDS;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    setStatus("loading");

    try {
      const formData = new FormData(form);
      formData.append("leadType", leadType || "");
      formData.append("leadTypeLabel", leadTypeLabel);

      Object.entries(thousandValues).forEach(([name, value]) => {
        formData.set(name, value.replace(/\s/g, ""));
      });

      const response = await fetch("/php/send_retail.php", {
        method: "POST",
        body: formData,
      });

      if (response.status === 200) {
        setStatus("success");
        navigate("/koszonjuk-lakossagi", {
          state: { formType: leadTypeLabel },
        });
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <Container id="ajanlat" className="d-flex justify-content-center p-0">
      <Card className="card form-card">
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="lead-form"
        >
          {fields.map((field) => (
            <Form.Group key={field.name} className="field-group">
              <Form.Label className="field-label">{field.label}</Form.Label>
              <div className="field-input-wrap">
                <FontAwesomeIcon icon={field.icon} className="field-icon" />
                {field.thousandSeparated ? (
                  <Form.Control
                    name={field.name}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9\s]*"
                    value={thousandValues[field.name] || ""}
                    onChange={(e) =>
                      handleThousandChange(field.name, e.target.value)
                    }
                    placeholder={field.placeholder}
                    required
                  />
                ) : (
                  <Form.Control
                    name={field.name}
                    type={field.type}
                    min={field.min}
                    placeholder={field.placeholder}
                    required
                  />
                )}
              </div>
              <Form.Control.Feedback type="invalid">
                {field.feedback}
              </Form.Control.Feedback>
            </Form.Group>
          ))}

          <Form.Group className="field-group">
            <Form.Label className="field-label d-block">
              Szerepel(t) a KHR-ben (negatív listán)?
            </Form.Label>
            <div className="khr-toggle">
              <Form.Check
                type="radio"
                id="khr-nem"
                name="khr"
                value="Nem"
                label="Nem"
                required
              />
              <Form.Check
                type="radio"
                id="khr-igen"
                name="khr"
                value="Igen"
                label="Igen"
                required
              />
            </div>
          </Form.Group>

          <Button
            className="btn btn-primary lead-form-submit"
            type="submit"
            disabled={status === "loading" || status === "success"}
          >
            {status === "loading" ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Küldés...
              </>
            ) : status === "success" ? (
              <>
                <FontAwesomeIcon icon={faCircleCheck} /> Sikeres beküldés
              </>
            ) : (
              <>
                Kérem az ingyenes tanácsadást{" "}
                <FontAwesomeIcon icon={faArrowRight} />
              </>
            )}
          </Button>

          {status === "error" && (
            <div className="lead-form-alert">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                Sikertelen beküldés. Kérjük írj emailt nekünk:{" "}
                <strong>financials@financials.hu</strong>
              </span>
            </div>
          )}

          <div className="lead-form-privacy">
            <FontAwesomeIcon icon={faLock} />
            <span>
              A regisztráció elküldésével elfogadom az{" "}
              <a className="form-section-a" href="/adatkezelesi-tajekoztato">
                adatkezelési tájékoztatóban
              </a>{" "}
              foglaltakat.
            </span>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
